import { getFormattedDisplayNameForEqlField } from '@modules/common/components/EqlFieldsAutoComplete/helpers';
import {
  useIsUnifiedFieldsEnabled,
  useShouldRemoveFileUploadPrefix,
} from '@modules/common/hooks';
import {
  DEFAULT_COMPARE_DURATION_A_NAME,
  DEFAULT_COMPARE_DURATION_B_NAME,
  DEFAULT_COMPARE_FILTER_A_NAME,
  DEFAULT_COMPARE_FILTER_B_NAME,
} from '@modules/quantify/constants';
import { useGetQuantifyFormulaDisplayName } from '@modules/quantify/hooks';
import { useGetCompareColors } from '@modules/quantify/hooks/useGetCompareColors';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { isAggregateFormula } from '@modules/quantify/utils';
import { CircleRounded } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import { EQLFunctionName } from 'v2/core/repository/eql/types';

export const CompareBarChartXAxisLabel = () => {
  const { colorA, colorB } = useGetCompareColors();
  const compareTo = useQuantifyStore(({ query }) => query.compareTo);
  const formulaID = useQuantifyStore(({ query }) => query.formulaID);
  const measuredAsCriteria = useQuantifyStore(
    ({ query }) => query.measuredAsCriteria,
  );
  const compareFilterAName = useQuantifyStore(
    ({ metadata }) =>
      metadata.compareQBField.compareQBFieldAName ||
      DEFAULT_COMPARE_FILTER_A_NAME,
  );
  const compareFilterBName = useQuantifyStore(
    ({ metadata }) =>
      metadata.compareQBField.compareQBFieldBName ||
      DEFAULT_COMPARE_FILTER_B_NAME,
  );
  const compareDurationAName = useQuantifyStore(
    ({ metadata }) =>
      metadata.compareDurationField.compareDurationAName ||
      DEFAULT_COMPARE_DURATION_A_NAME,
  );
  const compareDurationBName = useQuantifyStore(
    ({ metadata }) =>
      metadata.compareDurationField.compareDurationBName ||
      DEFAULT_COMPARE_DURATION_B_NAME,
  );

  const { formulaDisplayName } = useGetQuantifyFormulaDisplayName({
    shouldIncludeGroupByName: true,
  });

  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();
  const isUnifiedFieldEnabled = useIsUnifiedFieldsEnabled();

  const getXAxisLabel = () => {
    if (isAggregateFormula(formulaID)) {
      return formulaDisplayName;
    }

    const formulaUnit =
      formulaID === EQLFunctionName.NormaliseByPercent ? '%' : '#';

    return `${formulaUnit} ${getFormattedDisplayNameForEqlField({
      eqlField: measuredAsCriteria,
      shouldRemoveFileUploadPrefix,
      isUnifiedFieldEnabled,
    })}`;
  };

  const compareByLabelA =
    compareTo?.type === 'whereClause'
      ? compareFilterAName
      : compareDurationAName;
  const compareByLabelB =
    compareTo?.type === 'whereClause'
      ? compareFilterBName
      : compareDurationBName;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      spacing={3}
    >
      <Typography variant="caption" color="text.primary">
        {getXAxisLabel()}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Stack direction="row" alignItems="center" spacing={0.6}>
          <CircleRounded sx={{ color: colorA, width: 12 }} />
          <Typography variant="caption">{compareByLabelA}</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <CircleRounded sx={{ color: colorB, width: 12 }} />
          <Typography variant="caption">{compareByLabelB}</Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};
